import { useEffect, useMemo, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api, formatSAR, formatDate, validateCustomerPhone } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Printer, Sparkles, Receipt as ReceiptIcon, Loader2, Star, Trophy, History, MessageCircle, FileText, Download, Banknote, CreditCard, SplitSquareHorizontal } from "lucide-react";
import DailyReport from "@/components/DailyReport";
import TodaysTransactions from "@/components/TodaysTransactions";
import InvoiceShareModal from "@/components/InvoiceShareModal";
import KasirDailyReport from "@/components/KasirDailyReport";
import { useAuth } from "@/contexts/AuthContext";

function Receipt({ tx, t, onNew, onPrint }) {
  const [shareOpen, setShareOpen] = useState(false);
  const { lang } = useLang();
  const method = tx.payment_method || "cash";
  const payMethodLabel = method === "split"
    ? `${t("kasir.paySplit")} · ${t("kasir.payCash")} ${formatSAR(tx.cash_amount || 0)} + ${t("kasir.payCard")} ${formatSAR(tx.card_amount || 0)}`
    : (method === "card" ? t("kasir.payCard") : t("kasir.payCash"));
  // Pick the service name field that matches the active UI language.
  const itemName = (it) =>
    (lang === "ar" && it.service_name_ar) ||
    (lang === "en" && it.service_name_en) ||
    it.service_name_id;
  // Normalize phone for WhatsApp deep link
  const waPhone = (() => {
    const digits = (tx.customer_phone || "").replace(/[^\d]/g, "");
    if (!digits) return "";
    return digits.startsWith("0") ? `966${digits.slice(1)}` : digits;
  })();

  function sendWhatsApp() {
    const ln = (s) => `${s}\n`;
    const fmt = (n) => `${Number(n).toFixed(2)} SAR`;
    const localeMap = { id: "id-ID", ar: "ar-SA", en: "en-GB" };
    const dateStr = new Date(tx.created_at).toLocaleString(localeMap[lang] || "id-ID", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
    let msg = "";
    msg += ln("✨ *JAVA SPA · RIYADH* ✨");
    msg += ln("Prince Ahmed bin Abdulaziz Rd, King Fahd");
    msg += ln("📞 +966 55 222 1465  ·  ⏰ Open 24h");
    msg += ln("━━━━━━━━━━━━━━━━━━━━");
    msg += ln(`🧾 *${t("waMsg.title")}*`);
    msg += ln(`${t("waMsg.no")}: #${tx.id.slice(0, 8).toUpperCase()}`);
    msg += ln(`${t("receipt.issued")}: ${dateStr}`);
    msg += ln(`${t("receipt.customer")}: ${tx.customer_phone}`);
    msg += ln(`${t("receipt.cashier")}: ${tx.cashier_name}`);
    msg += ln("━━━━━━━━━━━━━━━━━━━━");
    msg += ln(`*${t("waMsg.services")}*`);
    tx.items.forEach((it, i) => {
      msg += ln(`${i + 1}. ${itemName(it)}`);
      msg += ln(`   👤 ${it.therapist_name}`);
      msg += ln(`   ${fmt(it.price)}`);
    });
    msg += ln("━━━━━━━━━━━━━━━━━━━━");
    msg += ln(`${t("kasir.subtotal")}:   ${fmt(tx.subtotal)}`);
    if (tx.discount_amount > 0) {
      msg += ln(`${t("kasir.discount")}:    -${fmt(tx.discount_amount)}`);
    }
    msg += ln(`*${t("kasir.total")}:     ${fmt(tx.total)}*`);
    msg += ln(`${t("kasir.paymentMethod")}: ${payMethodLabel}`);
    msg += ln("━━━━━━━━━━━━━━━━━━━━");
    msg += ln(t("waMsg.thanks1"));
    msg += ln(t("waMsg.thanks2"));

    const url = waPhone
      ? `https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="card-luxury p-6" data-testid="receipt-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-heading flex items-center gap-2">
          <ReceiptIcon className="h-5 w-5 text-gold" /> {t("kasir.receipt")}
        </h2>
        <span className="text-xs text-foreground/40">#{tx.id.slice(0, 8)}</span>
      </div>
      <div className="text-sm text-foreground/60 mb-4">
        <div>{formatDate(tx.created_at)}</div>
        <div>📞 {tx.customer_phone}</div>
        <div>{t("receipt.cashier")}: {tx.cashier_name}</div>
        <div>{t("kasir.paymentMethod")}: <span className="text-gold font-medium">{payMethodLabel}</span></div>
      </div>
      <div className="border-t border-gold/20 pt-4 space-y-2 text-sm">
        {tx.items.map((it, i) => (
          <div key={`${it.service_id}-${it.therapist_id}-${i}`} className="flex justify-between">
            <div>
              <div>{itemName(it)}</div>
              <div className="text-xs text-foreground/50">→ {it.therapist_name}</div>
            </div>
            <div>{formatSAR(it.price)}</div>
          </div>
        ))}
      </div>
      <div className="border-t border-gold/20 mt-4 pt-4 space-y-1 text-sm">
        <div className="flex justify-between"><span>{t("kasir.subtotal")}</span><span>{formatSAR(tx.subtotal)}</span></div>
        {tx.discount_amount > 0 && (
          <div className="flex justify-between text-leaf"><span>{t("kasir.discount")}</span><span>-{formatSAR(tx.discount_amount)}</span></div>
        )}
        <div className="flex justify-between text-xl font-heading text-gold pt-2 border-t border-gold/10">
          <span>{t("kasir.total")}</span><span>{formatSAR(tx.total)}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-6">
        <button
          onClick={() => setShareOpen(true)}
          data-testid="receipt-invoice-pdf-btn"
          className="inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-charcoal-900 border border-gold/40 hover:border-gold text-gold font-medium text-sm transition active:scale-95"
        >
          <Download className="h-4 w-4" /> {t("kasir.downloadPdf")}
        </button>
        <button
          onClick={() => setShareOpen(true)}
          data-testid="receipt-invoice-wa-btn"
          className="inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-leaf hover:bg-leaf-light text-white font-medium text-sm shadow-[0_8px_24px_-6px_rgba(109,190,69,0.45)] active:scale-95 transition"
        >
          <FileText className="h-4 w-4" /> {t("kasir.sendWhatsapp")}
        </button>
        <button
          onClick={sendWhatsApp}
          data-testid="receipt-wa-btn"
          className="inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-charcoal-900 border border-leaf/40 hover:border-leaf text-leaf font-medium text-sm transition active:scale-95"
          title={t("kasir.waQuickTip")}
        >
          <MessageCircle className="h-4 w-4" /> {t("kasir.waQuick")}
        </button>
        <button onClick={onPrint} className="btn-outline-gold justify-center text-sm" data-testid="receipt-print-btn">
          <Printer className="h-4 w-4" /> {t("kasir.print")}
        </button>
      </div>
      <button
        onClick={onNew}
        data-testid="receipt-new-btn"
        className="btn-gold w-full justify-center mt-3"
      >
        + {t("kasir.new")}
      </button>

      {shareOpen && (
        <InvoiceShareModal
          tx={tx}
          customerName={tx.customer_name || ""}
          paymentMethod={payMethodLabel}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}

export default function KasirPage() {
  const { t, lang } = useLang();
  const [services, setServices] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const PHONE_PREFIX = "+966 ";
  const [customerPhone, setCustomerPhone] = useState(PHONE_PREFIX);
  const [items, setItems] = useState([{ service_id: "", therapist_id: "", is_upsell: false, bonus_5: false, bonus_10: false }]);
  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [splitCash, setSplitCash] = useState("");
  const [splitCard, setSplitCard] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [visitInfo, setVisitInfo] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  // Live-derived phone validation state (recomputed every render — cheap).
  // Used by the input (border color + inline error) AND the submit buttons
  // (disabled state) so the kasir cannot submit an invalid number.
  const _trimmedPhone = customerPhone.trim();
  const _phoneEmpty = !_trimmedPhone || _trimmedPhone === PHONE_PREFIX.trim();
  const _phoneCheck = _phoneEmpty ? null : validateCustomerPhone(customerPhone);
  const phoneValid = !!_phoneCheck?.ok;
  const phoneErrKey = _phoneCheck && !_phoneCheck.ok
    ? (_phoneCheck.reason === "spa" ? "kasir.phoneSpaForbidden" : "kasir.phoneInvalid")
    : null;
  // Admin/Owner may backdate a missed transaction to a past datetime.
  // Empty string = "now" (default). String value = <input type="datetime-local">.
  const { user: authUser } = useAuth();
  const canBackdate = authUser?.role === "admin" || authUser?.role === "owner";
  const [backdate, setBackdate] = useState("");

  // Debounced customer lookup whenever the phone changes
  useEffect(() => {
    const phone = customerPhone.trim();
    // Skip when only the dialing-code prefix is present
    if (phone.length < 3 || phone === PHONE_PREFIX.trim()) { setVisitInfo(null); return; }
    setLookupLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers/lookup", { params: { phone } });
        setVisitInfo(data);
      } catch { setVisitInfo(null); }
      finally { setLookupLoading(false); }
    }, 450);
    return () => clearTimeout(handle);
  }, [customerPhone]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/therapists").then((r) => setTherapists(r.data));
  }, []);

  const enriched = items.map((it) => {
    const svc = services.find((s) => s.id === it.service_id);
    const th = therapists.find((u) => u.id === it.therapist_id);
    return { ...it, svc, th };
  });

  const subtotal = enriched.reduce((s, x) => s + (x.svc?.price || 0), 0);
  const discountAmt = useMemo(() => {
    if (discountType === "percent") return Math.min((subtotal * (discountValue || 0)) / 100, subtotal);
    if (discountType === "amount") return Math.min(discountValue || 0, subtotal);
    return 0;
  }, [discountType, discountValue, subtotal]);
  const total = Math.max(subtotal - discountAmt, 0);

  function updateItem(i, patch) {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  }
  function addItem() { setItems((p) => [...p, { service_id: "", therapist_id: "", is_upsell: false, bonus_5: false, bonus_10: false }]); }
  function removeItem(i) { setItems((p) => p.filter((_, idx) => idx !== i)); }

  function resetForm() {
    setCustomerPhone(PHONE_PREFIX); setItems([{ service_id: "", therapist_id: "", is_upsell: false, bonus_5: false, bonus_10: false }]);
    setDiscountType("none"); setDiscountValue(0);
    setPaymentMethod("cash"); setSplitCash(""); setSplitCard("");
    setReceipt(null);
  }

  async function submit() {
    if (!customerPhone.trim() || customerPhone.trim() === PHONE_PREFIX.trim()) return toast.error(t("kasir.customerPhone"));
    const phoneCheck = validateCustomerPhone(customerPhone);
    if (!phoneCheck.ok) {
      return toast.error(t(phoneCheck.reason === "spa" ? "kasir.phoneSpaForbidden" : "kasir.phoneInvalid"));
    }
    if (items.some((it) => !it.service_id || !it.therapist_id))
      return toast.error(`${t("kasir.selectService")} & ${t("kasir.selectTherapist")}`);

    const payloadItems = enriched.map((it) => ({
      service_id: it.svc.id,
      service_name_id: it.svc.name_id,
      service_name_ar: it.svc.name_ar,
      price: it.svc.price,
      therapist_id: it.th.id,
      therapist_name: it.th.name,
      is_upsell: !!(it.bonus_5 || it.bonus_10 || it.is_upsell),
      bonus_5: !!it.bonus_5,
      bonus_10: !!it.bonus_10,
    }));
    const upsell_item_ids = enriched.filter((it) => it.is_upsell).map((it) => it.svc.id);

    // Pre-compute expected grand total so we can validate split payment client-side
    const subtotalPreview = payloadItems.reduce((s, it) => s + it.price, 0);
    let discountAmt = 0;
    if (discountType === "percent") discountAmt = Math.round(subtotalPreview * (Number(discountValue) / 100) * 100) / 100;
    else if (discountType === "amount") discountAmt = Math.min(Number(discountValue) || 0, subtotalPreview);
    const totalPreview = Math.round(Math.max(subtotalPreview - discountAmt, 0) * 100) / 100;

    let cashAmt = 0, cardAmt = 0;
    if (paymentMethod === "split") {
      cashAmt = Number(splitCash) || 0;
      cardAmt = Number(splitCard) || 0;
      if (cashAmt <= 0 || cardAmt <= 0) {
        return toast.error(t("kasir.splitBothRequired"));
      }
      if (Math.abs(cashAmt + cardAmt - totalPreview) > 0.01) {
        return toast.error(`${t("kasir.splitSumMismatch")}: ${(cashAmt + cardAmt).toFixed(2)} ≠ ${totalPreview.toFixed(2)}`);
      }
    }

    setSubmitting(true);
    try {
      // Convert `datetime-local` (interpreted as Riyadh time) → ISO with +03:00 offset.
      // Empty string ⇒ backend uses server "now".
      let createdAtIso = undefined;
      if (canBackdate && backdate) {
        createdAtIso = `${backdate}:00+03:00`;
      }
      const { data } = await api.post("/transactions", {
        customer_phone: phoneCheck.phone,
        items: payloadItems,
        discount_type: discountType,
        discount_value: Number(discountValue) || 0,
        upsell_item_ids,
        payment_method: paymentMethod,
        cash_amount: cashAmt,
        card_amount: cardAmt,
        ...(createdAtIso ? { created_at: createdAtIso } : {}),
      });
      setReceipt(data);
      toast.success(t("kasir.success"));
      setBackdate(""); // clear after successful submit
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  }

  function printReceipt() {
    window.print();
  }

  const svcLabel = (s) => `${lang === "ar" ? s.name_ar : (lang === "en" ? (s.name_en || s.name_id) : s.name_id)} — ${formatSAR(s.price)}`;

  if (receipt) return <Receipt tx={receipt} t={t} onNew={resetForm} onPrint={printReceipt} />;

  return (
    <div className="max-w-5xl space-y-6 pb-32 md:pb-6">
      <div>
        <span className="text-xs uppercase tracking-[0.3em] text-gold">— POS</span>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading mt-2">{t("kasir.title")}</h1>
      </div>

      {/* Daily infographic report — collapsible, printable as PDF */}
      <DailyReport />

      {/* Kasir's own daily shift report (per-cashier) — with Excel + PDF export */}
      <KasirDailyReport />

      {/* Today's transactions list — with cancel/change actions */}
      <TodaysTransactions
        refreshKey={refreshKey}
        onChangeStart={({ customer_phone }) => {
          setCustomerPhone(customer_phone || PHONE_PREFIX);
          setItems([{ service_id: "", therapist_id: "", is_upsell: false, bonus_5: false, bonus_10: false }]);
          setDiscountType("none");
          setDiscountValue(0);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />

      <div className="card-luxury p-4 sm:p-6 space-y-5">
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">{t("kasir.customerPhone")}</label>
          <input
            data-testid="kasir-customer-phone"
            type="tel"
            inputMode="numeric"
            maxLength={PHONE_PREFIX.length + 9}
            value={customerPhone}
            onChange={(e) => {
              // Enforce the "+966 " prefix stickiness and cap 9 digits after it.
              // User can only edit the digits; prefix is never deletable.
              let v = e.target.value;
              if (!v.startsWith(PHONE_PREFIX)) {
                const digits = v.replace(/\D/g, "").replace(/^966/, "");
                v = PHONE_PREFIX + digits;
              }
              const after = v.slice(PHONE_PREFIX.length).replace(/\D/g, "").slice(0, 9);
              setCustomerPhone(PHONE_PREFIX + after);
            }}
            onFocus={(e) => {
              if (!e.target.value) setCustomerPhone(PHONE_PREFIX);
              setTimeout(() => {
                try { e.target.setSelectionRange(e.target.value.length, e.target.value.length); } catch (_) { /* ignore */ }
              }, 0);
            }}
            placeholder="+966 5XXXXXXXX"
            aria-invalid={phoneErrKey ? "true" : "false"}
            className={`w-full bg-charcoal-900 border rounded-lg px-4 py-3 outline-none focus:ring-2 transition-colors ${
              phoneErrKey
                ? "border-red-500/70 focus:border-red-500 focus:ring-red-500/30"
                : phoneValid
                  ? "border-leaf/60 focus:border-leaf focus:ring-leaf/30"
                  : "border-charcoal-600 focus:border-gold focus:ring-gold/30"
            }`}
          />
          {phoneErrKey && (
            <p data-testid="phone-error" className="mt-2 text-xs text-red-400">
              {t(phoneErrKey)}
            </p>
          )}

          {/* Customer visit notification */}
          {visitInfo && customerPhone.trim().length >= 3 && (
            <div
              data-testid="visit-notification"
              className={`mt-3 rounded-lg border p-3 flex items-start gap-3 animate-fade-in-up ${
                visitInfo.vip
                  ? "bg-gold/10 border-gold/40"
                  : visitInfo.visit_count === 0
                    ? "bg-leaf/10 border-leaf/30"
                    : "bg-charcoal-900/60 border-gold/15"
              }`}
            >
              <div className={`shrink-0 h-10 w-10 rounded-full grid place-items-center ${
                visitInfo.vip ? "bg-gold/25 text-gold"
                  : visitInfo.visit_count === 0 ? "bg-leaf/20 text-leaf"
                  : "bg-charcoal-700 text-foreground/70"
              }`}>
                {visitInfo.vip ? <Trophy className="h-5 w-5" /> :
                 visitInfo.visit_count === 0 ? <Star className="h-5 w-5" /> :
                 <History className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                {visitInfo.visit_count === 0 && (
                  <div className="font-medium text-leaf">{t("visit.newCustomer")}</div>
                )}
                {visitInfo.visit_count > 0 && !visitInfo.vip && (
                  <div className="font-medium">
                    {t("visit.returning").replace("{n}", visitInfo.next_visit_no)}
                  </div>
                )}
                {visitInfo.vip && (
                  <div className="font-medium text-gold">
                    {t("visit.vipReached").replace("{n}", visitInfo.visit_count)}
                  </div>
                )}
                {visitInfo.next_is_milestone && visitInfo.visit_count > 0 && (
                  <div className="text-xs text-gold mt-0.5">{t("visit.nextMilestone")}</div>
                )}
                {visitInfo.visit_count > 0 && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-foreground/60">
                    {visitInfo.favorite_service && (
                      <span>⭐ {t("visit.favorite")}: <span className="text-foreground/80">{visitInfo.favorite_service}</span></span>
                    )}
                    {visitInfo.total_spent > 0 && (
                      <span>💰 {t("visit.spent")}: <span className="text-gold">{formatSAR(visitInfo.total_spent)}</span></span>
                    )}
                    {visitInfo.last_visit && (
                      <span>📅 {t("visit.lastVisit")}: <span className="text-foreground/80">{formatDate(visitInfo.last_visit).split(",")[0]}</span></span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {lookupLoading && !visitInfo && customerPhone.trim().length >= 3 && (
            <div className="mt-2 text-xs text-foreground/40">{t("common.loading")}</div>
          )}
        </div>

        <div className="space-y-3">
          {items.map((it, i) => {
            const svc = services.find((s) => s.id === it.service_id);
            return (
              <div key={i} className="grid md:grid-cols-12 gap-3 items-start p-4 bg-charcoal-900/50 rounded-lg border border-gold/10" data-testid={`kasir-item-${i}`}>
                <div className="md:col-span-5">
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">{t("kasir.service")}</label>
                  <select
                    data-testid={`kasir-service-${i}`}
                    value={it.service_id}
                    onChange={(e) => updateItem(i, { service_id: e.target.value })}
                    className="w-full bg-charcoal-800 border border-charcoal-600 rounded-md px-3 py-2 outline-none focus:border-gold"
                  >
                    <option value="">{`— ${t("kasir.selectService")} —`}</option>
                    {services.map((s) => <option key={s.id} value={s.id}>{svcLabel(s)}</option>)}
                  </select>
                </div>
                <div className="md:col-span-4">
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">{t("kasir.therapist")}</label>
                  <select
                    data-testid={`kasir-therapist-${i}`}
                    value={it.therapist_id}
                    onChange={(e) => updateItem(i, { therapist_id: e.target.value })}
                    className="w-full bg-charcoal-800 border border-charcoal-600 rounded-md px-3 py-2 outline-none focus:border-gold"
                  >
                    <option value="">{`— ${t("kasir.selectTherapist")} —`}</option>
                    {therapists.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2 pt-1 flex flex-col gap-1.5">
                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid={`kasir-bonus5-${i}`}
                      checked={!!it.bonus_5}
                      onChange={(e) => updateItem(i, { bonus_5: e.target.checked, is_upsell: e.target.checked || !!it.bonus_10 })}
                      className="accent-leaf h-4 w-4"
                    />
                    <span className="text-leaf">Bonus (+5%)</span>
                  </label>
                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid={`kasir-bonus10-${i}`}
                      checked={!!it.bonus_10}
                      onChange={(e) => updateItem(i, { bonus_10: e.target.checked, is_upsell: e.target.checked || !!it.bonus_5 })}
                      className="accent-gold h-4 w-4"
                    />
                    <span className="text-gold">Bonus (+10%)</span>
                  </label>
                </div>
                <div className="md:col-span-1 pt-5 flex justify-end">
                  {items.length > 1 && (
                    <button
                      onClick={() => removeItem(i)}
                      data-testid={`kasir-remove-${i}`}
                      className="p-2 rounded-full hover:bg-red-500/10 text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {svc && (
                  <div className="md:col-span-12 text-xs text-foreground/50 -mt-2">
                    {svc.duration_min} {t("common.minutes")} · {formatSAR(svc.price)}
                  </div>
                )}
              </div>
            );
          })}
          <button onClick={addItem} data-testid="kasir-add-item-btn" className="btn-outline-gold w-full justify-center">
            <Plus className="h-4 w-4" /> {t("kasir.addItem")}
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">{t("kasir.discount")}</label>
            <select
              data-testid="kasir-discount-type"
              value={discountType}
              onChange={(e) => setDiscountType(e.target.value)}
              className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2"
            >
              <option value="none">{t("kasir.none")}</option>
              <option value="percent">{t("kasir.discountPercent")}</option>
              <option value="amount">{t("kasir.discountAmount")}</option>
            </select>
          </div>
          {discountType !== "none" && (
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">
                {discountType === "percent" ? "%" : "SAR"}
              </label>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                data-testid="kasir-discount-value"
                value={discountValue === 0 ? "" : discountValue}
                onChange={(e) => setDiscountValue(e.target.value === "" ? 0 : Number(e.target.value))}
                onFocus={(e) => e.target.select()}
                placeholder="0"
                className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2"
              />
            </div>
          )}
        </div>

        {/* Admin/Owner: Backdate transaction (for missed entries) */}
        {canBackdate && (
          <div className="rounded-lg border border-gold/25 bg-gold/5 p-3">
            <label htmlFor="kasir-backdate" className="block text-[11px] uppercase tracking-[0.2em] text-gold mb-2">
              Waktu Transaksi (Admin / Owner)
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                id="kasir-backdate"
                type="datetime-local"
                data-testid="kasir-backdate-input"
                value={backdate}
                onChange={(e) => setBackdate(e.target.value)}
                className="bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2 text-sm min-w-[220px]"
              />
              {backdate && (
                <button
                  type="button"
                  onClick={() => setBackdate("")}
                  data-testid="kasir-backdate-clear"
                  className="text-xs px-3 py-2 rounded-md border border-foreground/20 text-foreground/70 hover:border-foreground/40"
                >
                  Reset ke sekarang
                </button>
              )}
              <span className="text-[11px] text-foreground/50 flex-1 min-w-[200px]">
                {backdate
                  ? "Transaksi ini akan dicatat pada waktu di atas (waktu Riyadh)."
                  : "Kosongkan untuk memakai waktu sekarang. Isi hanya jika mencatat transaksi terlewat."}
              </span>
            </div>
          </div>
        )}

        {/* Payment method selector */}
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">{t("kasir.paymentMethod")}</label>
          <div className="grid grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => setPaymentMethod("cash")}
              data-testid="kasir-pay-cash"
              className={`flex items-center justify-center gap-2 py-3 rounded-lg border-2 transition active:scale-95 ${
                paymentMethod === "cash"
                  ? "border-gold bg-gold/10 text-gold"
                  : "border-charcoal-600 bg-charcoal-900 text-foreground/60 hover:border-gold/40"
              }`}
            >
              <Banknote className="h-5 w-5" />
              <span className="font-medium">{t("kasir.payCash")}</span>
            </button>
            <button
              type="button"
              onClick={() => setPaymentMethod("card")}
              data-testid="kasir-pay-card"
              className={`flex items-center justify-center gap-2 py-3 rounded-lg border-2 transition active:scale-95 ${
                paymentMethod === "card"
                  ? "border-gold bg-gold/10 text-gold"
                  : "border-charcoal-600 bg-charcoal-900 text-foreground/60 hover:border-gold/40"
              }`}
            >
              <CreditCard className="h-5 w-5" />
              <span className="font-medium">{t("kasir.payCard")}</span>
            </button>
            <button
              type="button"
              onClick={() => setPaymentMethod("split")}
              data-testid="kasir-pay-split"
              className={`flex items-center justify-center gap-2 py-3 rounded-lg border-2 transition active:scale-95 ${
                paymentMethod === "split"
                  ? "border-gold bg-gold/10 text-gold"
                  : "border-charcoal-600 bg-charcoal-900 text-foreground/60 hover:border-gold/40"
              }`}
            >
              <SplitSquareHorizontal className="h-5 w-5" />
              <span className="font-medium">{t("kasir.paySplit")}</span>
            </button>
          </div>

          {paymentMethod === "split" && (() => {
            const cashN = Number(splitCash) || 0;
            const cardN = Number(splitCard) || 0;
            const sum = cashN + cardN;
            const diff = total - sum;
            const isValid = cashN > 0 && cardN > 0 && Math.abs(diff) < 0.01;
            return (
              <div className="mt-3 grid grid-cols-2 gap-3" data-testid="kasir-split-inputs">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-leaf mb-1">
                    <Banknote className="h-3 w-3 inline me-1" /> {t("kasir.payCash")} (SAR)
                  </label>
                  <input
                    type="number" min="0" step="0.01" inputMode="decimal"
                    value={splitCash}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSplitCash(v);
                      if (v !== "" && total > 0) {
                        const rem = Math.max(total - (Number(v) || 0), 0);
                        setSplitCard(rem > 0 ? rem.toFixed(2) : "");
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    data-testid="kasir-split-cash"
                    className="w-full bg-charcoal-900 border border-leaf/30 focus:border-leaf rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-gold mb-1">
                    <CreditCard className="h-3 w-3 inline me-1" /> {t("kasir.payCard")} (SAR)
                  </label>
                  <input
                    type="number" min="0" step="0.01" inputMode="decimal"
                    value={splitCard}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSplitCard(v);
                      if (v !== "" && total > 0) {
                        const rem = Math.max(total - (Number(v) || 0), 0);
                        setSplitCash(rem > 0 ? rem.toFixed(2) : "");
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    placeholder="0"
                    data-testid="kasir-split-card"
                    className="w-full bg-charcoal-900 border border-gold/30 focus:border-gold rounded-md px-3 py-2"
                  />
                </div>
                <div className={`col-span-2 text-xs px-3 py-2 rounded-md ${isValid ? "bg-leaf/10 text-leaf" : "bg-red-500/10 text-red-300"}`} data-testid="kasir-split-summary">
                  {isValid
                    ? `✓ Tunai ${formatSAR(cashN)} + Kartu ${formatSAR(cardN)} = ${formatSAR(sum)}`
                    : `${sum > 0 ? `Total input ${formatSAR(sum)}` : "Belum ada input"} — kurang ${formatSAR(Math.max(diff, 0))}${diff < 0 ? ` (lebih ${formatSAR(-diff)})` : ""}`}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Total panel — visible on tablet/desktop; mobile uses sticky bottom bar */}
        <div className="hidden md:block bg-charcoal-900/70 border border-gold/20 rounded-xl p-5 space-y-2">
          <div className="flex justify-between text-sm"><span>{t("kasir.subtotal")}</span><span data-testid="kasir-subtotal">{formatSAR(subtotal)}</span></div>
          {discountAmt > 0 && (
            <div className="flex justify-between text-sm text-leaf"><span>{t("kasir.discount")}</span><span>-{formatSAR(discountAmt)}</span></div>
          )}
          <div className="flex justify-between pt-2 border-t border-gold/10">
            <span className="font-heading text-lg">{t("kasir.total")}</span>
            <span className="font-heading text-2xl text-gold" data-testid="kasir-total">{formatSAR(total)}</span>
          </div>
        </div>

        <button
          onClick={submit}
          disabled={submitting || !phoneValid}
          data-testid="kasir-submit-btn"
          className="hidden md:flex btn-gold w-full justify-center text-base disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
          {t("kasir.submit")}
        </button>
      </div>

      {/* MOBILE sticky bottom action bar */}
      <div className="md:hidden fixed inset-x-0 bottom-[60px] z-30 bg-charcoal-900/95 backdrop-blur-xl border-t border-gold/30 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-[0_-8px_24px_-4px_rgba(0,0,0,0.5)]">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">{t("kasir.total")}</div>
            <div className="text-2xl font-heading text-gold leading-tight truncate" data-testid="kasir-mobile-total">{formatSAR(total)}</div>
            {discountAmt > 0 && (
              <div className="text-[10px] text-leaf">−{formatSAR(discountAmt)} {t("kasir.discount")}</div>
            )}
          </div>
          <button
            onClick={submit}
            disabled={submitting || !phoneValid}
            data-testid="kasir-mobile-submit"
            className="btn-gold py-3 px-5 shrink-0 disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
            <span className="text-sm">{t("kasir.submit")}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
